import { getNodeText } from '@/common/utils/formTitle'
import {
  PLATFORM_DIALOG_MIN_WIDTH,
  PLATFORM_DIALOG_DEFAULT_VIEWPORT_RATIO,
  clampDialogWidth,
  clampDialogWidthMax,
  clampDialogHeight,
  clampDialogHeightMax,
  clampDialogResizeWidth,
  clampDialogResizeHeight,
  estimateFormContentWidth,
  estimateFormDialogHeight,
  formHasTableElements,
} from '@/common/utils/estimateFormContentWidth'
import {
  loadSavedDialogSize,
  saveDialogSize,
} from '@/common/utils/platformDialogSizeStorage'

/** Нижняя граница дефолта  */
export const PLATFORM_DIALOG_DEFAULT_WIDTH = PLATFORM_DIALOG_MIN_WIDTH

/** Swing {@code SwingWindows.DEF_HEIGHT}. */
export const PLATFORM_DIALOG_DEFAULT_HEIGHT = 600

export interface PlatformDialogViewport {
  width: number
  height: number
}

export interface PlatformDialogSize {
  widthPx: number
  heightPx: number | null
}

export interface ResolvePlatformDialogSizeOptions {
  formId?: string | number | null
  viewport?: PlatformDialogViewport
  ignoreSaved?: boolean
}

const parseFormWindowDimension = (
  raw: unknown,
  screenSize: number,
): number | null => {
  const value = getNodeText(raw).trim()
  if (!value || value === '0') {
    return null
  }

  if (value.endsWith('%')) {
    const percent = Number.parseInt(value.slice(0, -1), 10)
    if (!Number.isFinite(percent) || percent <= 0) {
      return null
    }
    return Math.round(screenSize * (percent / 100))
  }

  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

const defaultViewport = (): PlatformDialogViewport => ({
  width: typeof window !== 'undefined' ? window.innerWidth : 1920,
  height: typeof window !== 'undefined' ? window.innerHeight : 1080,
})

/** Дефолт без XML: шире на больших мониторах (~92% viewport, не уже min). */
export const resolveDefaultDialogWidth = (viewport: PlatformDialogViewport): number =>
  clampDialogWidth(
    Math.max(
      PLATFORM_DIALOG_MIN_WIDTH,
      Math.round(viewport.width * PLATFORM_DIALOG_DEFAULT_VIEWPORT_RATIO),
    ),
    viewport,
  )

const resolveEstimatedWidth = (
  form: Record<string, unknown> | null | undefined,
  viewport: PlatformDialogViewport,
): number => {
  const viewportDefault = resolveDefaultDialogWidth(viewport)
  const estimated = estimateFormContentWidth(form)
  // Content estimate alone often stays ~1200 (2-col table) — never narrower than viewport default.
  if (estimated <= 0) {
    return viewportDefault
  }
  return clampDialogWidth(Math.max(viewportDefault, estimated), viewport)
}

/**
 * Resolves platform form dialog size (Swing getDialogSize parity + web content estimate).
 * Width: XML > metadata/viewport default; saved only if ≥ that (не откатываем к legacy-узким prefs).
 * Height: XML > table-form estimate; saved only if ≥ that.
 */
export const resolvePlatformDialogSize = (
  form: Record<string, unknown> | null | undefined,
  options?: ResolvePlatformDialogSizeOptions | PlatformDialogViewport,
): PlatformDialogSize => {
  const normalizedOptions: ResolvePlatformDialogSizeOptions =
    options && 'width' in options && 'height' in options && !('formId' in options)
      ? { viewport: options as PlatformDialogViewport }
      : (options as ResolvePlatformDialogSizeOptions | undefined) ?? {}

  const viewport = normalizedOptions.viewport ?? defaultViewport()
  const saved = normalizedOptions.ignoreSaved
    ? { widthPx: null, heightPx: null }
    : loadSavedDialogSize(normalizedOptions.formId)

  const xmlWidth = parseFormWindowDimension(form?.width, viewport.width)
  const xmlHeight = parseFormWindowDimension(form?.height, viewport.height)

  const preferredWidth = (() => {
    if (xmlWidth != null) {
      return clampDialogWidthMax(xmlWidth, viewport)
    }
    return clampDialogWidth(resolveEstimatedWidth(form, viewport), viewport)
  })()

  const widthPx = (() => {
    // Старые сохранённые 800–1200 схлопывали диалог после flash большого дефолта.
    if (saved.widthPx != null && saved.widthPx >= preferredWidth) {
      return clampDialogResizeWidth(saved.widthPx, viewport)
    }
    return preferredWidth
  })()

  const preferredHeight = (() => {
    if (xmlHeight != null) {
      return clampDialogHeightMax(xmlHeight, viewport)
    }
    return estimateFormDialogHeight(form, viewport)
  })()

  const heightPx = (() => {
    if (saved.heightPx == null) {
      return preferredHeight
    }
    if (preferredHeight == null) {
      return clampDialogResizeHeight(saved.heightPx, viewport)
    }
    if (saved.heightPx >= preferredHeight) {
      return clampDialogResizeHeight(saved.heightPx, viewport)
    }
    return preferredHeight
  })()

  return { widthPx, heightPx }
}

export {
  clampDialogWidth,
  clampDialogHeight,
  estimateFormContentWidth,
  estimateFormDialogHeight,
  formHasTableElements,
  saveDialogSize,
  loadSavedDialogSize,
}
